import React, { useState, useEffect, memo } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'primereact/button'

import {
    useCompanyGetChargePointsWithNfcInfoQuery,
    ChargePoint,
    useCompanySetChargePointsNfcInfoMutation,
    ChargePointNfcInfo,
} from '../../../common/redux/api/web-api'
import { Table, TableBody, TableHeader, TableBodyCell, TableRow } from '../../../lib/table'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { WidgetLayout } from '../../../lib/widget-layout'
import Toggle from '../../../common/components/Toggle'
import { RootState } from '../../redux/store'
import Text from '../../../ui/Text'
import { filterFieldsBySearchValue } from '../../../lib/filter-fields-by-search-value'
import useText from '../../../common/hooks/useText'

const ButtonContainer = styled.div`
    margin-bottom: 20px;
    justify-content: flex-end;
    display: flex;
    gap: 4px;
`

const ToggleContainer = styled.span`
    display: flex;
    flex-direction: column;
`

const ToggleItem = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;
`

export const UserChargePointsPage = memo(function UserChargepointsPage(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const [chargePoints, setChargePoints] = useState<ChargePoint[]>([])
    const [changedChargePoints, setChangedChargePoints] = useState<ChargePointNfcInfo[]>([])

    const adaptConnectorNaming = useAdaptConnectorNaming()

    const { id: userId } = useParams() as { id: string }
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const [searchValue, setSearchValue] = useState('')

    const { data: initialChargePoints, isFetching } = useCompanyGetChargePointsWithNfcInfoQuery(
        { userId, companyId },
        { refetchOnMountOrArgChange: true }
    )
    const [updateChargepointsNfcAccess, { isLoading: saving }] = useCompanySetChargePointsNfcInfoMutation()

    useEffect(() => {
        if (initialChargePoints) setChargePoints(initialChargePoints)
    }, [initialChargePoints])

    const updateNfcAccess = (
        chargePointId: string | null | undefined,
        connectorId: number | undefined,
        hasNfcAccess?: boolean
    ) => {
        const newChargePointArray = chargePoints.map((c) => {
            if (c.id === chargePointId) {
                return {
                    ...c,
                    connectors: c?.connectors?.map((conn) => {
                        if (conn.connectorId === connectorId) return { ...conn, hasNfcAccess }
                        return conn
                    }),
                }
            }
            return c
        })
        setChargePoints(newChargePointArray)

        let isExisting = false
        const tempChanged = changedChargePoints.map((c) => {
            if (c.chargePointId === chargePointId && c.connectorId === connectorId) {
                isExisting = true
                return {
                    ...c,
                    hasNfcAccess,
                }
            }
            return c
        })

        if (!isExisting) {
            tempChanged.push({ chargePointId, connectorId, hasNfcAccess })
        }
        setChangedChargePoints(tempChanged)
    }

    const save = async () => {
        try {
            await updateChargepointsNfcAccess({ companyId, userId, body: changedChargePoints }).unwrap()
            notifySuccess(text('lblRFIDAccessToChargersUpdated'), true)
            navigate(`/user/${userId}`)
        } catch (e) {
            console.error(e)
            notifyError(text('lblSomethingWentWrong'), true)
        }
    }

    return (
        <PageLayoutContainer>
            <ButtonContainer>
                <Button
                    type="button"
                    label={text('btnSave')}
                    icon="pi pi-save"
                    data-testid="btnSave"
                    loading={saving}
                    onClick={() => save()}
                ></Button>
                <Button
                    type="button"
                    label={text('btnBack')}
                    icon="pi pi-arrow-left"
                    outlined
                    onClick={() => navigate(`/user/${userId}`)}
                    data-testid="btnBack"
                ></Button>
            </ButtonContainer>
            <WidgetLayout title={text('lblChargers')}>
                <div className="row margin-bottom-small">
                    <div className="col-xs-12 col-md-4">
                        <input
                            data-testid="search"
                            type="text"
                            name="search"
                            id="search"
                            className="form-control"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder={text('lblSearch')}
                        />
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableBodyCell data-testid="h-cell-id">
                                <Text code="lblId" />
                            </TableBodyCell>
                            <TableBodyCell data-testid="h-cell-name">
                                <Text code="lblName" />
                            </TableBodyCell>
                            <TableBodyCell data-testid="h-cell-access">
                                <Text code="lblRFIDAccess" />
                            </TableBodyCell>
                        </TableRow>
                    </TableHeader>
                    {!isFetching && (
                        <TableBody>
                            {chargePoints
                                ?.filter((c) => filterFieldsBySearchValue([c.id, c.name ?? ''], searchValue))
                                ?.map((c) => (
                                    <TableRow key={c.id}>
                                        <TableBodyCell data-testid="b-cell-id">{c.id}</TableBodyCell>
                                        <TableBodyCell data-testid="b-cell-name">{c.name}</TableBodyCell>
                                        <TableBodyCell data-testid="b-cell-access">
                                            <ToggleContainer>
                                                {c?.connectors?.map((conn) => {
                                                    if (conn.connectorFunctionType === 'Charger')
                                                        return (
                                                            <ToggleItem key={conn.connectorId}>
                                                                <Toggle
                                                                    data-testid={`connector-toggle-${conn.connectorId}`}
                                                                    key={conn.connectorId}
                                                                    size="md"
                                                                    isToggled={conn?.hasNfcAccess ?? false}
                                                                    toggleFn={() => {
                                                                        updateNfcAccess(
                                                                            c.id,
                                                                            conn.connectorId,
                                                                            !conn?.hasNfcAccess
                                                                        )
                                                                    }}
                                                                ></Toggle>
                                                                {adaptConnectorNaming(conn.name)}
                                                            </ToggleItem>
                                                        )
                                                })}
                                            </ToggleContainer>
                                        </TableBodyCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    )}
                </Table>
                {isFetching && <div className="shimmer-container shimmer-small"></div>}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
